import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  inject,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { coerceBooleanProperty, BooleanInput } from '@angular/cdk/coercion';

@Component({
  selector: 'sg-toggle',
  templateUrl: './sg-toggle.component.html',
  styleUrls: ['./sg-toggle.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgToggleComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SgToggleComponent implements ControlValueAccessor, OnDestroy, AfterViewInit {
  static ngAcceptInputType_checked: BooleanInput;
  static ngAcceptInputType_disabled: BooleanInput;

  @ViewChild('inputEl') inputEl: ElementRef<HTMLInputElement>;

  @Input() label: string;
  @Input() size: 'default' | 'small' = 'default';

  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: BooleanInput) {
    const newValue = coerceBooleanProperty(value);
    if (this._checked !== newValue) {
      this._checked = newValue;
      this.cdr.markForCheck();
    }
  }
  private _checked = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _disabled = false;

  @Output() readonly sgToggleClick = new EventEmitter<boolean>();
  @Output() readonly checkedChange = new EventEmitter<boolean>();

  focused = false;
  hasContent = false;

  private focusMonitor = inject(FocusMonitor);
  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit() {
    this.focusMonitor.monitor(this.inputEl, true).subscribe((origin: FocusOrigin) => {
      this.focused = origin === 'keyboard';
      this.cdr.markForCheck();
    });
  }

  ngAfterContentInit() {
    const el = this.elementRef.nativeElement as HTMLElement;
    this.hasContent =
      el.childNodes.length > 0 &&
      Array.from(el.childNodes).some((node) =>
        node.nodeType === Node.TEXT_NODE ? node.textContent?.trim() : node.nodeType === Node.ELEMENT_NODE
      );
  }

  onInputChange(event: Event) {
    if (this.disabled) return;

    const input = event.target as HTMLInputElement;
    this._checked = input.checked;
    this.onChange(this._checked);
    this.checkedChange.emit(this._checked);
    this.sgToggleClick.emit(this._checked);
    this.onTouched();
  }

  writeValue(value: boolean): void {
    this._checked = !!value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.focusMonitor.stopMonitoring(this.inputEl);
  }
}
